import { useReactiveVar } from '@apollo/client'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import {
    contentTeamProjectId,
    isContentTeam,
    isOmWorkspaceUser,
} from 'api/graphQL/reactiveVars/workspaceContent'
import React from 'react'
import { __RouterContext } from 'react-router'
import { RouteComponentProps } from 'react-router-dom'

/**
 * Custom hook to determine if the user is currently viewing the OM (Office Master) Workspace.
 *
 * The OM Workspace has special behavior:
 * - Allows editing of inactive content
 * - Hides track changes styling
 * - Shows visibility toggles for content activation/deactivation
 *
 * @returns {Object} OM workspace state
 * @returns {boolean} isCurrentlyOnOMWorkspace - True when an OM user is on their firm's best-practice project, or a content-team user is on the content-team OM project.
 * @returns {boolean} isOMUser - True if the current user has OM workspace access
 *
 * @example
 * ```tsx
 * const { isCurrentlyOnOMWorkspace, isOMUser } = useOMWorkspace()
 *
 *
 * // Allow editing inactive content in OM workspace
 * const isEditMode = shouldEdit && (isCurrentlyOnOMWorkspace ? true : !inactive)
 * ```
 */
export const useOMWorkspace = () => {
    const isOMUser = useReactiveVar(isOmWorkspaceUser)
    const isContentTeamUser = useReactiveVar(isContentTeam)
    const contentTeamProject = useReactiveVar(contentTeamProjectId)
    const { currentUserActiveFirm } = React.useContext(AppBarContext)
    const routerContext = React.useContext(__RouterContext) as
        | RouteComponentProps<{ projectId: string }>
        | undefined
    const pathname =
        routerContext?.location.pathname ?? window.location.pathname
    // Falls back to the URL when there's no matched-Route projectId (e.g. AppHeader sits above the routed subtree).
    const pathSegments = pathname.split('/').filter(Boolean)
    const projectId =
        routerContext?.match?.params.projectId ??
        (pathSegments[0] === 'projects' ? pathSegments[1] : undefined)
    // True when an OM user is on their firm's best-practice project, or a
    // content-team user is on the content-team OM project.
    const isCurrentlyOnOMWorkspace = Boolean(
        projectId != null &&
            ((isContentTeamUser && projectId === contentTeamProject) ||
                (isOMUser &&
                    currentUserActiveFirm?.bestPracticeProjectId === projectId))
    )

    return {
        /** True when the current user+project combo is the OM workspace (see above). */
        isCurrentlyOnOMWorkspace,

        /**
         * True if the current user has OM workspace access.
         */
        isOMUser,
    }
}
